import { cva, type VariantProps } from "class-variance-authority";
import type { FC, ReactNode } from "react";
import { cn } from "../../../utils";
import BadgeChip from "./BadgeChip";
import computeTitle, { type TitleVariant } from "./computeTitle";
import ProviderLink from "./ProviderLink";

const cardThumbnailVariants = cva(
  [
    "max-w-full group relative flex justify-center overflow-hidden",
    "rounded-lg bg-neutral-800",
    "[&_img]:!relative [&_img]:!h-auto",
    "[&_img]:rounded-lg [&_img]:aspect-[210/282]",
  ],
  {
    variants: {
      size: {
        auto: "w-auto",
        normal: "w-[104px] md:w-[124px] lg:w-[140px]",
      },
    },
    defaultVariants: {
      size: "normal",
    },
  },
);

export type CardThumbnailBadge = {
  label: string;
  color: string;
  gradient?: string;
};

export type CardThumbnailProvider = {
  name: string;
  link?: ReactNode;
};

const TITLE_VARIANT_STYLES: Record<TitleVariant, string> = {
  oneRow:
    "text-[18px] leading-[0.97] md:text-[21px] md:leading-[0.93] lg:text-[24px] lg:leading-[0.93]",
  twoRow:
    "text-[16px] leading-[0.97] md:text-[20px] md:leading-[0.93] lg:text-[22px] lg:leading-[0.93]",
  threeRow:
    "text-[14px] leading-[0.97] md:text-[18px] md:leading-[0.93] lg:text-[21px] lg:leading-[0.93]",
  fourRow:
    "text-[12px] leading-[0.97] md:text-[14px] md:leading-[0.93] lg:text-[17px] lg:leading-[0.93]",
};

export type CardThumbnailProps = VariantProps<typeof cardThumbnailVariants> & {
  id?: string;
  className?: string;
  link: ReactNode;
  hover?: ReactNode;
  badge?: CardThumbnailBadge;
  title?: string;
  provider?: CardThumbnailProvider;
};

const CardThumbnail: FC<CardThumbnailProps> = ({
  id,
  className,
  size,
  link,
  hover,
  badge,
  title,
  provider,
}) => {
  const computedTitle = title ? computeTitle(title) : null;
  const showOverlay = title != null || provider != null;

  return (
    <div
      id={id}
      data-slot="card-thumbnail"
      data-size={size ?? "normal"}
      className={cn(cardThumbnailVariants({ size }), className)}
    >
      {hover}
      {link}
      {badge && <BadgeChip badge={badge} />}
      {showOverlay && (
        <>
          <div
            data-slot="card-thumbnail-gradient"
            className="absolute inset-0 bg-[linear-gradient(180deg,transparent_31.73%,#000_100%)] pointer-events-none z-[1]"
            aria-hidden
          />
          <div
            data-slot="card-thumbnail-overlay"
            className="absolute inset-0 z-[2] flex flex-col items-center px-2 md:px-0 lg:px-2 pb-2 pointer-events-none"
          >
            <div
              data-slot="card-thumbnail-info"
              className="mt-auto h-[59px] flex flex-col items-center justify-end gap-1 w-[96px] md:w-[124px]"
            >
              {computedTitle && (
                <p
                  data-slot="card-thumbnail-title"
                  className={cn(
                    "text-center uppercase font-black font-big-shoulders text-white whitespace-pre-line",
                    TITLE_VARIANT_STYLES[computedTitle.variant],
                  )}
                >
                  {computedTitle.displayTitle}
                </p>
              )}
              {provider && (
                <div data-slot="card-thumbnail-provider" className="opacity-80">
                  <ProviderLink provider={provider} />
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CardThumbnail;
